/* ──────────────────────────────────────────────────────────────────────────
 * Crowd safety — density, Level of Service, and command escalation.
 *
 * This is the layer that separates a dashboard from a crowd-safety system.
 *
 * Percentage occupancy ("Ramkund is 92% full") is a capacity-planning figure.
 * It cannot tell you whether people can still move, and movement is what
 * decides whether a dense crowd stays a crowd or becomes a crush. The
 * measure that does is AREAL DENSITY — persons per square metre of usable
 * standing area — which is what Hajj crowd management at Mina and the
 * Jamarat Bridge is built around, and what every major crowd-safety
 * methodology (Fruin, Still, the UK Event Safety Guide) uses.
 *
 * The distinction is not academic. A sector can read 70% "full" against a
 * planning capacity and still be at 5 persons/m² on the ghat steps, which is
 * the density at which people stop being able to control their own movement
 * and progressive crowd collapse becomes possible. Conversely a sector at
 * 95% of a generous capacity may be perfectly safe. Only density answers the
 * question an incident commander is actually asking.
 *
 * ── HONESTY, BECAUSE THIS PARTICULAR NUMBER INVITES FALSE CONFIDENCE ──
 * Everything here is computed from SIMULATED occupancy over ASSUMED assembly
 * areas. Real deployment needs surveyed usable area per zone and a real
 * count. A density figure looks authoritative in a way a percentage does
 * not — an operator will act on "5.1 p/m²" — so every output carries its
 * provenance and its assumed area, and the UI shows both.
 * ────────────────────────────────────────────────────────────────────────── */

import { TRUST } from "../data/provenance";
import { SECTORS } from "../data/kumbhGeo";
import { FLOW_VECTORS } from "../data/operational";

/* ── Usable assembly area per sector, in square metres ─────────────────
 * NOT the sector footprint. A sector polygon covers roads, buildings and
 * river; only a fraction is standing area where a crowd actually collects.
 * Using the polygon area would understate density by two orders of
 * magnitude and make every zone look safe.
 *
 * These figures are ASSUMPTIONS for the demo, sized to be plausible for a
 * ghat apron, a temple forecourt or a staging ground. A real deployment
 * replaces them with surveyed area from the department's site plan. */
export const ASSEMBLY_AREA_M2 = {
  "SEC-01": 26000,  // Ramkund ghat apron and approach steps
  "SEC-02": 48000,  // Panchavati — larger, more dispersed
  "SEC-03": 18000,  // Trimbakeshwar temple forecourt — tightest space
  "SEC-04": 52000,  // Tapovan open ground
  "SEC-05": 30000,  // Nashik Road corridor and station forecourt
  "SEC-06": 60000,  // Gangapur staging ground
};

/* ── Level of Service bands ────────────────────────────────────────────
 * Density thresholds in persons/m² for a standing/assembly crowd, with the
 * operational meaning of each. The bands follow the widely used crowd-safety
 * progression rather than Fruin's walkway LoS, because these zones are
 * assembly areas, not corridors.
 *
 * `action` is deliberately part of the band definition: a level that does
 * not tell an operator what changes at that level is just a colour. */
export const LOS_BANDS = [
  {
    id: "A", maxDensity: 1.0, color: "#10B981",
    label: { en: "Free flow", mr: "मुक्त प्रवाह" },
    meaning: { en: "Individuals move freely and choose their own speed.",
               mr: "व्यक्ती मुक्तपणे फिरू शकतात." },
    action: { en: "Normal monitoring.", mr: "नेहमीचे निरीक्षण." },
    command: "Bronze",
  },
  {
    id: "B", maxDensity: 2.0, color: "#84CC16",
    label: { en: "Comfortable", mr: "आरामदायक" },
    meaning: { en: "Movement restricted slightly; passing others requires care.",
               mr: "हालचालीवर किंचित मर्यादा." },
    action: { en: "Normal monitoring. Watch inflow rate.", mr: "नेहमीचे निरीक्षण, आवक पहा." },
    command: "Bronze",
  },
  {
    id: "C", maxDensity: 3.0, color: "#F59E0B",
    label: { en: "Busy", mr: "गजबजलेले" },
    meaning: { en: "Walking speed restricted. Cross-flow becomes difficult.",
               mr: "चालण्याचा वेग मर्यादित." },
    action: { en: "Meter entry. Brief stewards. Prepare diversion.",
              mr: "प्रवेश नियंत्रित करा, वळण तयार ठेवा." },
    command: "Silver",
  },
  {
    id: "D", maxDensity: 4.0, color: "#F97316",
    label: { en: "Crowded", mr: "गर्दीचे" },
    meaning: { en: "Movement only at the crowd's pace. Unintentional contact is constant.",
               mr: "केवळ गर्दीच्या गतीने हालचाल." },
    action: { en: "Restrict inflow now. Open additional exits. Silver command active.",
              mr: "आवक थांबवा, अतिरिक्त निर्गमन उघडा." },
    command: "Silver",
  },
  {
    id: "E", maxDensity: 5.0, color: "#DC2626",
    label: { en: "Critical", mr: "गंभीर" },
    meaning: { en: "Individuals cannot control their own movement. Shockwaves can propagate.",
               mr: "व्यक्ती स्वतःची हालचाल नियंत्रित करू शकत नाहीत." },
    action: { en: "STOP inflow. Decompress from upstream. Gold command.",
              mr: "आवक थांबवा, वरच्या बाजूने दाब कमी करा." },
    command: "Gold",
  },
  {
    id: "F", maxDensity: Infinity, color: "#7F1D1D",
    label: { en: "Crush risk", mr: "चेंगराचेंगरीचा धोका" },
    meaning: { en: "Progressive crowd collapse possible. People may be lifted off their feet.",
               mr: "गर्दी कोसळण्याची शक्यता." },
    action: { en: "EMERGENCY. Halt all inflow, evacuate downstream, full Gold command.",
              mr: "आणीबाणी — सर्व आवक थांबवा, निर्वासन करा." },
    command: "Gold",
  },
];

export function bandForDensity(d) {
  return LOS_BANDS.find((b) => d < b.maxDensity) || LOS_BANDS[LOS_BANDS.length - 1];
}

/* Command tiers, the structure used for major incident management: Bronze
 * operational, Silver tactical, Gold strategic. Naming the tier turns a
 * density reading into a statement about WHO should be acting. */
export const COMMAND_TIERS = {
  Bronze: { rank: 0, label: { en: "Bronze — operational", mr: "ब्राँझ — कार्यान्वयन" },
            color: "#0891B2" },
  Silver: { rank: 1, label: { en: "Silver — tactical", mr: "सिल्व्हर — रणनीतिक" },
            color: "#F97316" },
  Gold:   { rank: 2, label: { en: "Gold — strategic", mr: "गोल्ड — धोरणात्मक" },
            color: "#DC2626" },
};

/* Net inbound persons/min for a sector — mirrors the risk engine's rule so
 * the two cannot disagree about which way a crowd is moving. */
function netPerMin(sectorId, flows) {
  const mine = flows.filter((f) => f.from === sectorId);
  let inRate = 0;
  let outRate = 0;
  mine.forEach((f) => {
    const inbound = f.bearing >= 315 || f.bearing < 135;
    if (inbound) inRate += f.rate; else outRate += f.rate;
  });
  return (inRate - outRate) / 60;
}

/* ── Per-sector crowd safety assessment ────────────────────────────────── */
export function assessSector(sector, flows = FLOW_VECTORS) {
  const areaM2 = ASSEMBLY_AREA_M2[sector.id] || null;
  if (!areaM2) {
    return {
      sectorId: sector.id,
      name: sector.name,
      centre: sector.centre,
      areaM2: null,
      density: null,
      band: null,
      /* No assumed area means no density. Reporting one anyway would be
         inventing the denominator, which is the easiest way to produce a
         confident and completely wrong safety figure. */
      unavailable: "No assembly area defined for this sector.",
      trust: TRUST.SIMULATED,
    };
  }

  const density = sector.current / areaM2;
  const band = bandForDensity(density);
  const net = netPerMin(sector.id, flows);

  /* Minutes until the NEXT band, at the current net rate. This is the
     number that creates a decision window — "you are at D and reach E in
     14 minutes" is actionable in a way "you are at D" is not. */
  let minutesToNextBand = null;
  if (net > 0 && Number.isFinite(band.maxDensity)) {
    const peopleToNextBand = band.maxDensity * areaM2 - sector.current;
    minutesToNextBand = Math.max(0, Math.round(peopleToNextBand / net));
  }

  const nextBand = Number.isFinite(band.maxDensity)
    ? LOS_BANDS[LOS_BANDS.indexOf(band) + 1] || null
    : null;

  return {
    sectorId: sector.id,
    name: sector.name,
    centre: sector.centre,
    areaM2,
    people: sector.current,
    density: Number(density.toFixed(2)),
    areaPerPerson: Number((areaM2 / sector.current).toFixed(2)),
    band,
    nextBand,
    minutesToNextBand,
    netPerMin: Math.round(net),
    command: band.command,
    trust: TRUST.SIMULATED,
  };
}

/* ── Theatre-wide crowd safety picture ─────────────────────────────────── */
export function crowdSafetyPicture({ flows = FLOW_VECTORS } = {}) {
  const sectors = SECTORS.map((s) => assessSector(s, flows))
    .filter((a) => a.density != null)
    .sort((a, b) => b.density - a.density);

  /* The theatre's command tier is the HIGHEST any single sector demands.
     Averaging would let one zone at crush risk hide behind five quiet ones,
     which is precisely the failure mode that kills people. */
  const tier = sectors.reduce(
    (worst, s) =>
      COMMAND_TIERS[s.command].rank > COMMAND_TIERS[worst].rank ? s.command : worst,
    "Bronze"
  );

  const atOrAboveD = sectors.filter(
    (s) => ["D", "E", "F"].includes(s.band.id)
  );

  /* Soonest band escalation anywhere — the theatre's tightest clock. */
  const soonest = sectors
    .filter((s) => s.minutesToNextBand != null && s.nextBand)
    .sort((a, b) => a.minutesToNextBand - b.minutesToNextBand)[0] || null;

  return {
    sectors,
    commandTier: tier,
    worst: sectors[0] || null,
    atOrAboveD,
    soonest,
    trust: TRUST.SIMULATED,
    assumptions: [
      "Density uses an ASSUMED usable assembly area per sector, not surveyed area. " +
        "Replace ASSEMBLY_AREA_M2 with the department's site plan before any operational use.",
      "Density is treated as uniform across a sector. Real crowds are not uniform — " +
        "the dangerous density is local, at a gate mouth or a stair head, and can be " +
        "double the sector mean.",
      "Occupancy counts are simulated; no sensor, CCTV or turnstile feed is connected.",
      "Band thresholds follow standard crowd-safety practice for assembly areas. " +
        "They are guidance, not a legal standard, and site-specific factors " +
        "(gradient, surface, exits, weather) shift them.",
    ],
  };
}
